import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { repository } from '@whizz/api-client';
import { useIdentity } from '@whizz/react-identity';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import classNames from 'classnames';
import { QUERIES_IDS } from '../../../util/vars';
import { Toggle } from '../../../components';
import { Mail } from '../../../components/icon/customIcons';

interface Props {
  classId?: number | null;
}

const getSubscribed = (val: any): boolean => {
  if (!val) return false;
  if (val.type === 'Buffer') return val.data[0] === 1;
  return Boolean(val);
};

const WeeklyEmailToggle = ({ classId }: Props): JSX.Element | null => {
  const { me } = useIdentity();
  const teacherId = (me as any)?.getId?.();
  const { translate } = useI18n();
  const queryClient = useQueryClient();

  const staffQuery = useQuery([
    QUERIES_IDS.STAFF,
  ], () => repository.StaffRepository.getStaff(), { enabled: !!teacherId, placeholderData: { classes: [], teachers: [], teachersClasses: [] } as any });

  const teacher = staffQuery.data?.teachers?.find((t: any) => t.id === teacherId);
  const teacherClass = staffQuery.data?.teachersClasses?.find((tc: any) => tc.teacherId === teacherId && tc.classId === classId);

  const initialChecked = useMemo(() => {
    if (teacherClass) return getSubscribed(teacherClass.isSubscribedToWeeklyUpdates);
    if (teacher) return teacher.isSubscribedToWeeklyUpdates || false;
    return false;
  }, [ teacherClass, teacher ]);

  const [ checked, setChecked ] = useState<boolean>(initialChecked);

  useEffect(() => setChecked(initialChecked), [ initialChecked ]);

  const mutation = useMutation(({ wantsToSubscribe }: { wantsToSubscribe: boolean }) => repository.NewTeacherRepository.updateIndividualSubscriptionWc({ teacherId, wantsToSubscribe, classId: classId as number }), {
    onSuccess: () => {
      queryClient.invalidateQueries([ QUERIES_IDS.CLASSES, teacherId ]);
      queryClient.invalidateQueries([ QUERIES_IDS.STAFF ]);
    },
    onError: () => {
      // revert
      setChecked((s) => !s);
    },
  });

  if (!classId) return null;

  const toggleLabel = translate('pages.HOME.WEEKLY_EMAIL.TOGGLE_LABEL');

  const handleToggle = (next: boolean) => {
    setChecked(next);
    mutation.mutate({ wantsToSubscribe: next });
  };

  return (
    <div data-cy="home_weekly_email_toggle">
      <div style={{ minWidth: 260 }} className={'bg-white rounded-default shadow px-md py-2 flex items-center gap-4'}>
        <div className={'flex-shrink-0 flex items-center justify-center'}>
          <Mail width={26} height={19} aria-hidden="true" focusable="false" />
        </div>
        <div className={'flex-shrink-0'}>
          <Toggle
            id="home_weekly_email_toggle_input"
            label={toggleLabel}
            color={'header'}
            isOnOff
            checked={checked}
            onChange={(event) => handleToggle(event.target.checked)}
          />
        </div>
        <div className={'flex-1'}>
          <div className={`font-bold text-sm transition-opacity ${classNames({ 'opacity-50' : !checked })}`}>{toggleLabel}</div>
          <div className={`text-xs text-muted transition-opacity ${classNames({ 'opacity-50' : !checked })}`}>{translate('pages.HOME.WEEKLY_EMAIL.TOGGLE_SUBLABEL')}</div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyEmailToggle;
